import { ReactElement, useEffect, useState } from 'react';
import {
  segmentTracking,
  pageUrl,
  productTitle,
} from 'src/apps/instana-roi/lib/tracker';
import { ArrowRight16 } from '@carbon/icons-react';
import {
  ModalWrapper,
  Slider,
  RadioButton,
  Checkbox,
  Button,
} from 'carbon-components-react';
import { FormattedMessage } from 'react-intl';
import {
  Disruptions,
  Developers,
  ITOpsEmployees,
  DisruptionBucketLessThan30Mins,
  DisruptionBucket30To90Mins,
  DisruptionBucketHours,
  DisruptionBucketDays,
  AverageTime,
  emailCOSJson,
} from 'src/apps/instana-roi/constants/defaults';
import messages from 'src/apps/instana-roi/locales/messages';
import { segmentsTracking } from 'src/common/lib/tracker';
import { getMessage } from 'src/common/hooks/widgets/useLang';
import Feedback from 'src/common/utils/Feedback';

interface AvgTimePerDistruptions {
  speedGain: number;
  reduction: number;
}

export interface BVAQuestionsProps {
  localExist: boolean;
  toggleCountry: boolean;
  modalLocalCountry: string;
  modalLocalLanguage: string;
  modalLocalCurrency: string;
  selectedLocale: string;
  disruptions: number;
  itOpsEmployees: number;
  developers: number;
  defaultCheckedAvgTime: boolean;
  prometheus: boolean;
  openTracing: boolean;
  openTelemetry: boolean;
  jaeger: boolean;
  zipkin: boolean;
  openCensus: boolean;
  setPrometheus: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenTracing: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenTelemetry: React.Dispatch<React.SetStateAction<boolean>>;
  setJaeger: React.Dispatch<React.SetStateAction<boolean>>;
  setZipkin: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenCensus: React.Dispatch<React.SetStateAction<boolean>>;
  setAvgTime: React.Dispatch<React.SetStateAction<string>>;
  setDefaultCheckedAvgTime: React.Dispatch<React.SetStateAction<boolean>>;
  setAvgTimePerDistruptions: React.Dispatch<
    React.SetStateAction<AvgTimePerDistruptions>
  >;
  setDisruptions: React.Dispatch<React.SetStateAction<number>>;
  setITOpsEmployees: React.Dispatch<React.SetStateAction<number>>;
  setDevelopers: React.Dispatch<React.SetStateAction<number>>;
  setViewBVAResults: React.Dispatch<React.SetStateAction<boolean>>;
  reset: () => void;
  resetTimer: () => void;
}

// Function to capture parameters
export default function BVAQuestions({
  localExist,
  toggleCountry,
  modalLocalCountry,
  modalLocalLanguage,
  modalLocalCurrency,
  selectedLocale,
  itOpsEmployees,
  disruptions,
  developers,
  prometheus,
  openTracing,
  openTelemetry,
  jaeger,
  zipkin,
  openCensus,
  setPrometheus,
  setOpenTracing,
  setOpenTelemetry,
  setJaeger,
  setZipkin,
  setOpenCensus,
  defaultCheckedAvgTime,
  setDefaultCheckedAvgTime,
  setAvgTime,
  setAvgTimePerDistruptions,
  setDisruptions,
  setITOpsEmployees,
  setViewBVAResults,
  setDevelopers,
  reset,
  resetTimer,
}: BVAQuestionsProps): ReactElement {
  const [maxDisruptions, setMaxDisruptions] = useState(false);
  const [minDisruptions, setMinDisruptions] = useState(false);
  const [maxItOps, setMaxItOps] = useState(false);
  const [minItOps, setMinItOps] = useState(false);
  const [maxDev, setMaxDev] = useState(false);
  const [minDev, setMinDev] = useState(false);
  /**
   * Define validation of the Input field of the sliders
   */
  useEffect(() => {
    const disruptionsId = document.getElementById(
      'disruptions-input-for-slider',
    );
    const developersId = document.getElementById('developers-input-for-slider');
    const itopsemployeesId = document.getElementById(
      'itopsemployees-input-for-slider',
    );
    /**
     * Define Listener
     */
    const addListener = (
      id: HTMLElement | null,
      max: number,
      min: number,
      setvalue: React.Dispatch<React.SetStateAction<number>>,
      setMax: React.Dispatch<React.SetStateAction<boolean>>,
      setMin: React.Dispatch<React.SetStateAction<boolean>>,
    ) => {
      if (id !== null) {
        id.addEventListener('input', (e) => {
          if (e != null) {
            const element = e.currentTarget as HTMLInputElement;
            if (element.value !== '') {
              if (Number.parseInt(element.value) > max) {
                element.value = max.toString();
                setvalue(max);
                setMax(true);
              } else if (Number.parseInt(element.value) < min) {
                element.value = min.toString();
                setvalue(min);
                setMin(true);
              }
            } else {
              element.value = '';
            }
          }
        });
      }
    };

    /**
     * Current number of disruptions per month
     */
    addListener(
      disruptionsId,
      Disruptions.max,
      Disruptions.min,
      setDisruptions,
      setMaxDisruptions,
      setMinDisruptions,
    );

    /**
     * Current no of developers in the organization
     */
    addListener(
      developersId,
      Developers.max,
      Developers.min,
      setDevelopers,
      setMaxDev,
      setMinDev,
    );

    /**
     * Current no of employees in the team
     */
    addListener(
      itopsemployeesId,
      ITOpsEmployees.max,
      ITOpsEmployees.min,
      setITOpsEmployees,
      setMaxItOps,
      setMinItOps,
    );
  }, []);

  /**
   * Handlers to check max and min and set value
   */
  const handleSliders = (
    value: number,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    max: number,
    min: number,
  ) => {
    resetTimer();
    if (value >= min && value <= max) {
      setValue(value);
    } else if (value > max) {
      setValue(max);
    } else {
      setValue(min);
    }
  };

  return (
    <div className="bx--row mar3 padding">
      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
        <div className="bx--row">
          <div
            className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12"
            id="countryModal"
          >
            {localExist && toggleCountry ? (
              <ModalWrapper
                modalLabel={` ${getMessage(
                  messages.foundCountry,
                )} ${modalLocalCountry}.
                    ${getMessage(
                      messages.accessPage,
                    )} ${modalLocalLanguage} ${modalLocalCurrency}
                    `}
                modalHeading={`${getMessage(messages.availableIn)}
                    ${modalLocalLanguage} ${getMessage(
                  messages.localCurrencie,
                )} ${modalLocalCurrency}`}
                size={'sm'}
                primaryButtonText={`${getMessage(messages.takeMeThere)}`}
                secondaryButtonText={`${getMessage(messages.question3)} `}
                buttonTriggerText={` ${getMessage(
                  messages.accessPageCapitalized,
                )} ${modalLocalLanguage}`}
                handleOpen={() => {
                  segmentTracking('CTA Clicked', {
                    pageTitle: 'Instana ROI Landing Page',
                    object: 'Pricing page',
                    resultValue: 'CTA clicked',
                    CTA: 'Instana ROI title',
                    location: 'Instana ROI Estimator',
                    action: '',
                    field: '',
                  });
                }}
                handleSubmit={() => {
                  localStorage.setItem('langLocale', selectedLocale);
                  (window.location.href = window.location.href =
                    'https://www.ibm.com/' +
                    selectedLocale +
                    '/products/instana'),
                    segmentTracking('CTA Clicked', {
                      pageTitle: 'Instana ROI Landing Page',
                      object: 'Pricing page',
                      resultValue: 'CTA clicked',
                      CTA: 'Instana ROI title',
                      location: 'Instana ROI Estimator',
                      action: '',
                      field: '',
                    });
                  return true;
                }}
                onSecondarySubmit={() => {
                  segmentTracking('CTA Clicked', {
                    pageTitle: 'Instana ROI Landing Page',
                    object: 'Pricing page',
                    resultValue: 'CTA clicked',
                    CTA: 'Stay here button clicked ' + modalLocalCountry,
                    location: 'Instana ROI Estimator',
                    action: '',
                    field: '',
                  });
                  return true;
                }}
              ></ModalWrapper>
            ) : null}
          </div>
        </div>
        <div className="bx--row">
          <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16">
            <div className="bx--row">
              <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                <p className="page_sub_heading">
                  <FormattedMessage {...messages.page_heading} />
                </p>
              </div>
            </div>
            <div className="bx--row mar3">
              <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                <p className="page_body">
                  <FormattedMessage {...messages.page_heading_2} />
                </p>
              </div>
            </div>
            <div className="bx--row mt-3">
              <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                {/* How many major application disruptions do you experience per month - Question 1 */}
                <div className="bx--row">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <Slider
                      id="disruptions"
                      min={Disruptions.min}
                      max={Disruptions.max}
                      inputType={'number'}
                      value={disruptions}
                      invalid={maxDisruptions || minDisruptions}
                      labelText={
                        <span className="page_body">
                          <FormattedMessage {...messages.question_1} />
                          {maxDisruptions == true ? (
                            <span className="slide_text">
                              <FormattedMessage {...messages.invalidVmText_1} />
                              {` ${Disruptions.max}`}
                              <FormattedMessage {...messages.invalidVmText_2} />
                            </span>
                          ) : minDisruptions == true ? (
                            <span className="slide_text">
                              <FormattedMessage {...messages.invalidVmText_3} />
                              {` ${Disruptions.min}`}
                              <FormattedMessage {...messages.invalidVmText_2} />
                            </span>
                          ) : null}
                        </span>
                      }
                      onBlur={() => {
                        null;
                      }}
                      onChange={({ value }) => {
                        handleSliders(
                          value,
                          setDisruptions,
                          Disruptions.max,
                          Disruptions.min,
                        );
                        setMaxDisruptions(false);
                        setMinDisruptions(false);

                        segmentTracking('CTA Clicked', {
                          pageTitle: 'Instana ROI Landing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'questions 1 slider',
                          location: 'Instana ROI Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    />
                  </div>
                </div>
                {/* On average how much time is spent resolving each major disruption - Question 2 */}
                <div className="bx--row mar4">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <p className="page_body">
                          <span className="page_body">
                            <FormattedMessage {...messages.question_2} />
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="bx--row mar6">
                      <div className="bx--col-lg-6 bx--col-md-3 bx--col-sm-2">
                        <RadioButton
                          labelText={
                            <FormattedMessage
                              {...messages.lessThan30MinsText}
                            />
                          }
                          value={AverageTime.LessThan30Mins}
                          id="avg-time-distruption-opt-1"
                          name="average-time-disruptions"
                          onChange={() => {
                            setAvgTimePerDistruptions(
                              DisruptionBucketLessThan30Mins,
                            );
                            setAvgTime(AverageTime.LessThan30Mins);
                            setDefaultCheckedAvgTime(false);
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Instana ROI Landing Page',
                              object: 'BVAQuestions Avg Hrs',
                              resultValue: 'CTA clicked',
                              CTA: 'Less than 30 hours',
                              location: 'Instana ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                      <div className="bx--col-lg-6 bx--col-md-3 bx--col-sm-2">
                        <RadioButton
                          labelText={
                            <FormattedMessage
                              {...messages.thirtyNinetyMinsText}
                            />
                          }
                          value={AverageTime.Mins30To90}
                          id="avg-time-distruption-opt-2"
                          name="average-time-disruptions"
                          onChange={() => {
                            setAvgTimePerDistruptions(
                              DisruptionBucket30To90Mins,
                            );
                            setAvgTime(AverageTime.Mins30To90);
                            setDefaultCheckedAvgTime(false);
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Instana ROI Landing Page',
                              object: 'BVAQuestions Avg Hrs',
                              resultValue: 'CTA clicked',
                              CTA: '30-90 mins',
                              location: 'Instana ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div className="bx--row mar6">
                      <div className="bx--col-lg-6 bx--col-md-3 bx--col-sm-2">
                        <RadioButton
                          labelText={
                            <FormattedMessage {...messages.hoursText} />
                          }
                          value={AverageTime.default}
                          id="avg-time-distruption-default"
                          name="average-time-disruptions"
                          onChange={() => {
                            setAvgTimePerDistruptions(DisruptionBucketHours);
                            setAvgTime(AverageTime.default);
                            setDefaultCheckedAvgTime(true);
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Instana ROI Landing Page',
                              object: 'BVAQuestions Avg Hrs',
                              resultValue: 'CTA clicked',
                              CTA: 'Hours',
                              location: 'Instana ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                          checked={defaultCheckedAvgTime}
                        />
                      </div>
                      <div className="bx--col-lg-6 bx--col-md-3 bx--col-sm-2">
                        <RadioButton
                          labelText={
                            <FormattedMessage {...messages.daysPlusText} />
                          }
                          value={AverageTime.Days}
                          id="avg-time-distruption-opt-4"
                          name="average-time-disruptions"
                          onChange={() => {
                            setAvgTimePerDistruptions(DisruptionBucketDays);
                            setAvgTime(AverageTime.Days);
                            setDefaultCheckedAvgTime(false);
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Instana ROI Landing Page',
                              object: 'BVAQuestions Avg Hrs',
                              resultValue: 'CTA clicked',
                              CTA: 'Day+',
                              location: 'Instana ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {/* How many IT Ops employees do you have in your team - Question 3 */}
                <div className="bx--row mar3">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <Slider
                      id="developers"
                      min={Developers.min}
                      max={Developers.max}
                      inputType={'number'}
                      value={developers}
                      invalid={maxDev || minDev}
                      labelText={
                        <span className="page_body">
                          <FormattedMessage {...messages.question_3} />
                          {maxDev == true ? (
                            <span className="slide_text">
                              <FormattedMessage {...messages.invalidVmText_1} />
                              {` ${Developers.max}`}
                              <FormattedMessage {...messages.invalidVmText_2} />
                            </span>
                          ) : minDev == true ? (
                            <span className="slide_text">
                              <FormattedMessage {...messages.invalidVmText_3} />
                              {` ${Developers.min}`}
                              <FormattedMessage {...messages.invalidVmText_2} />
                            </span>
                          ) : null}
                        </span>
                      }
                      onBlur={() => {
                        null;
                      }}
                      onChange={({ value }) => {
                        handleSliders(
                          value,
                          setDevelopers,
                          Developers.max,
                          Developers.min,
                        );
                        setMaxDev(false);
                        setMinDev(false);

                        segmentTracking('CTA Clicked', {
                          pageTitle: 'Instana ROI Landing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'How many developers are in your organization slider',
                          location: 'Instana ROI Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    />
                  </div>
                </div>
                {/* How many IT Ops employees do you have in your team - Question 4 */}
                <div className="bx--row mar4">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <Slider
                      id="itopsemployees"
                      min={ITOpsEmployees.min}
                      max={ITOpsEmployees.max}
                      inputType={'number'}
                      value={itOpsEmployees}
                      invalid={maxItOps || minItOps}
                      labelText={
                        <span className="page_body">
                          <FormattedMessage {...messages.question_4} />
                          {maxItOps == true ? (
                            <span className="slide_text">
                              <FormattedMessage {...messages.invalidVmText_1} />
                              {` ${ITOpsEmployees.max}`}
                              <FormattedMessage {...messages.invalidVmText_2} />
                            </span>
                          ) : minItOps == true ? (
                            <span className="slide_text">
                              <FormattedMessage {...messages.invalidVmText_3} />
                              {` ${ITOpsEmployees.min}`}
                              <FormattedMessage {...messages.invalidVmText_2} />
                            </span>
                          ) : null}
                        </span>
                      }
                      onBlur={() => {
                        null;
                      }}
                      onChange={({ value }) => {
                        handleSliders(
                          value,
                          setITOpsEmployees,
                          ITOpsEmployees.max,
                          ITOpsEmployees.min,
                        );
                        setMaxItOps(false);
                        setMinItOps(false);

                        segmentTracking('CTA Clicked', {
                          pageTitle: 'Instana ROI Landing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'How many IT Ops employees do you have in your team slider',
                          location: 'Instana ROI Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    />
                  </div>
                </div>
                {/* Do you use any of the following OpenSource technologies - Question 5 */}
                <div className="bx--row mar4">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <p className="page_body">
                          <span className="page_body">
                            <FormattedMessage {...messages.question_5} />
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="bx--row mar6">
                      <div className="bx--col-lg-6 bx--col-md-3 bx--col-sm-2">
                        <Checkbox
                          labelText={
                            <FormattedMessage {...messages.checkbox_1} />
                          }
                          id="prometheus"
                          checked={prometheus}
                          onChange={() => {
                            setPrometheus(!prometheus);
                            segmentsTracking('CTA Clicked', {
                              pageTitle: 'Instana ROI Estimator',
                              object: 'Open Source Technologies',
                              resultValue: 'CTA clicked',
                              CTA: 'prometheus Checked/Unchecked',
                              location: 'Instana ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                      <div className="bx--col-lg-6 bx--col-md-3 bx--col-sm-2">
                        <Checkbox
                          labelText={
                            <FormattedMessage {...messages.checkbox_2} />
                          }
                          id="jaeger"
                          checked={jaeger}
                          onChange={() => {
                            setJaeger(!jaeger);
                            segmentsTracking('CTA Clicked', {
                              pageTitle: 'Instana ROI Estimator',
                              object: 'Open Source Technologies',
                              resultValue: 'CTA clicked',
                              CTA: 'jaeger Checked/Unchecked',
                              location: 'Instana ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div className="bx--row mt-03">
                      <div className="bx--col-lg-6 bx--col-md-3 bx--col-sm-2">
                        <Checkbox
                          labelText={
                            <FormattedMessage {...messages.checkbox_3} />
                          }
                          id="openTelemetry"
                          checked={openTelemetry}
                          onChange={() => {
                            setOpenTelemetry(!openTelemetry);
                            segmentsTracking('CTA Clicked', {
                              pageTitle: 'Instana ROI Estimator',
                              object: 'Open Source Technologies',
                              resultValue: 'CTA clicked',
                              CTA: 'openTelemetry Checked/Unchecked',
                              location: 'Instana ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                      <div className="bx--col-lg-6 bx--col-md-3 bx--col-sm-2">
                        <Checkbox
                          labelText={
                            <FormattedMessage {...messages.checkbox_4} />
                          }
                          id="zipkin"
                          checked={zipkin}
                          onChange={() => {
                            setZipkin(!zipkin);
                            segmentsTracking('CTA Clicked', {
                              pageTitle: 'Instana ROI Estimator',
                              object: 'Open Source Technologies',
                              resultValue: 'CTA clicked',
                              CTA: 'zipkin Checked/Unchecked',
                              location: 'Instana ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div className="bx--row mt-03">
                      <div className="bx--col-lg-6 bx--col-md-3 bx--col-sm-2">
                        <Checkbox
                          labelText={
                            <FormattedMessage {...messages.checkbox_5} />
                          }
                          id="openTracing"
                          checked={openTracing}
                          onChange={() => {
                            setOpenTracing(!openTracing);
                            segmentsTracking('CTA Clicked', {
                              pageTitle: 'Instana ROI Estimator',
                              object: 'Open Source Technologies',
                              resultValue: 'CTA clicked',
                              CTA: 'openTracing Checked/Unchecked',
                              location: 'Instana ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                      <div className="bx--col-lg-6 bx--col-md-3 bx--col-sm-2">
                        <Checkbox
                          labelText={
                            <FormattedMessage {...messages.checkbox_6} />
                          }
                          id="openCensus"
                          checked={openCensus}
                          onChange={() => {
                            setOpenCensus(!openCensus);
                            segmentsTracking('CTA Clicked', {
                              pageTitle: 'Instana ROI Estimator',
                              object: 'BVAResults Page',
                              resultValue: 'CTA clicked',
                              CTA: 'OpenCensus Checked/Unchecked',
                              location: 'Instana ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Decisions */}
                <div className="bx--row mt-10">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 bva-buttons">
                    <Button
                      renderIcon={ArrowRight16}
                      kind="primary"
                      className="bx--col-lg-8 bx--col-md-4 bx--col-sm-4"
                      onClick={() => {
                        setViewBVAResults(true);
                        segmentsTracking('CTA Clicked', {
                          pageTitle: 'Instana ROI Estimator',
                          object: 'BVAResults Page',
                          resultValue: 'CTA clicked',
                          CTA: 'View BVA Results',
                          location: 'Instana ROI Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <FormattedMessage {...messages.bva_results} />
                    </Button>
                    <Button
                      kind="tertiary"
                      className="bx--col-lg-2 bx--col-md-2 bx--col-sm-4"
                      onClick={() => {
                        reset();
                        segmentsTracking('CTA Clicked', {
                          pageTitle: 'Instana ROI Estimator',
                          object: 'BVAResults Page',
                          resultValue: 'CTA clicked',
                          CTA: 'Reset',
                          location: 'Instana ROI Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <FormattedMessage {...messages.reset} />
                    </Button>
                  </div>
                  <div className="feedback-section">
                    <Feedback
                      pageTitle="Instana ROI Estimator"
                      object="Pricing Page"
                      messages={messages}
                      productTitle={productTitle}
                      pageUrl={pageUrl}
                      emailJson={emailCOSJson}
                    ></Feedback>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
